import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";

// Recepción directa: ya no se captura Orden de Compra. Se elige proveedor, telas
// (o habilitación) con su costo y los rollos; el back genera la OC automática,
// la compra, el cargo al proveedor y la entrada al inventario en una sola operación.
// `contenedor` solo cambia el título: en ambos casos los rollos se pueden capturar
// a mano o importar de Excel (columna "cantidad") por renglón.

const nuevoRollo = (cantidad = "") => ({ id: uuidv4(), cantidad });
const nuevoRenglonTela = () => ({ id: uuidv4(), telas: null, colores: "", precio: "", rollos: [nuevoRollo()] });
const nuevoRenglonHab = () => ({ id: uuidv4(), habilitacion: null, precio: "", cantidad: "" });

const num = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);

function ComprasDirectaCreate({ contenedor = false }) {
  const { user } = useContext(AuthContext);
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const URL_COLORES = process.env.REACT_APP_URL_COLORES;
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;
  const URL_TELAS = process.env.REACT_APP_URL_TELAS;
  const URL_HABILITACION = process.env.REACT_APP_URL_HABILITACION;

  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [proveedores, setProveedores] = useState([]);
  const [telas, setTelas] = useState([]);
  const [habilitaciones, setHabilitaciones] = useState([]);
  const [colores, setColores] = useState([]);

  const [tipo, setTipo] = useState("Tela");
  const [proveedor, setProveedor] = useState("");
  const [plazo, setPlazo] = useState("");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [factura_proveedor, setFacturaProveedor] = useState("NA");
  const [pais_origen, setPaisOrigen] = useState("Mexico");
  const [observaciones, setObservaciones] = useState("NA");
  const [impuestos, setImpuestos] = useState(false);
  const [impuestosPor, setImpuestosPor] = useState(16);

  const [renglones, setRenglones] = useState([nuevoRenglonTela()]);
  const [guardando, setGuardando] = useState(false);

  // Para pasar el foco al rollo nuevo al dar Enter
  const focoRef = useRef(null);
  useEffect(() => {
    if (focoRef.current) {
      const el = document.getElementById(focoRef.current);
      if (el) el.focus();
      focoRef.current = null;
    }
  });

  useEffect(() => {
    const get = (url, set) =>
      axios
        .get(url, { headers })
        .then((res) => set((res.data || []).filter((a) => a.is_active !== "No")))
        .catch((err) => console.log(err));
    get(URL_PROVEEDORES, setProveedores);
    get(URL_TELAS, setTelas);
    get(URL_HABILITACION, setHabilitaciones);
    get(URL_COLORES, setColores);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const telaById = useMemo(() => new Map(telas.map((t) => [t._id, t])), [telas]);
  const habById = useMemo(() => new Map(habilitaciones.map((h) => [h._id, h])), [habilitaciones]);

  // ---------- Totales (siempre derivados del estado, sin desfases) ----------
  const totales = useMemo(() => {
    const t = { metros: 0, kilos: 0, piezas: 0, rollos: 0, subtotal: 0 };
    renglones.forEach((r) => {
      let cant, unidad;
      if (tipo === "Tela") {
        const tela = r.telas ? telaById.get(r.telas) : null;
        unidad = tela ? tela.unidad : "";
        const vals = r.rollos.map((x) => num(x.cantidad)).filter((c) => c > 0);
        t.rollos += vals.length;
        cant = vals.reduce((s, c) => s + c, 0);
      } else {
        const hab = r.habilitacion ? habById.get(r.habilitacion) : null;
        unidad = hab ? hab.unidad : "";
        cant = num(r.cantidad);
      }
      if (unidad === "Metros") t.metros += cant;
      if (unidad === "Kilos") t.kilos += cant;
      if (unidad === "Piezas") t.piezas += cant;
      t.subtotal += cant * num(r.precio);
    });
    t.subtotal = r2(t.subtotal);
    t.iva = impuestos ? r2((t.subtotal * num(impuestosPor)) / 100) : 0;
    t.total = r2(t.subtotal + t.iva);
    return t;
  }, [renglones, tipo, telaById, habById, impuestos, impuestosPor]);

  // ---------- Encabezado ----------
  function cambiaProveedor(id) {
    setProveedor(id);
    const p = proveedores.find((a) => a._id === id);
    if (!p) {
      setPlazo("");
      return;
    }
    setPlazo(p.plazo != null ? p.plazo : "");
    // El % de impuesto del proveedor viene por default (se puede cambiar)
    const porc = p.impuesto != null ? num(p.impuesto) : 0;
    if (porc > 0) {
      setImpuestos(true);
      setImpuestosPor(porc);
    } else {
      setImpuestos(false);
    }
  }

  // Solo los proveedores del tipo elegido (Tela / Habilitacion, campo telaHab del proveedor)
  const proveedoresDelTipo = useMemo(
    () =>
      proveedores
        .filter((a) => a.telaHab === tipo)
        .sort((a, b) => String(a.nombre_comercial || "").localeCompare(String(b.nombre_comercial || ""))),
    [proveedores, tipo]
  );

  function cambiaTipo(nuevo) {
    if (nuevo === tipo) return;
    const hayDatos = renglones.some((r) => (tipo === "Tela" ? r.telas : r.habilitacion));
    const aplicar = () => {
      setTipo(nuevo);
      setRenglones([nuevo === "Tela" ? nuevoRenglonTela() : nuevoRenglonHab()]);
      // Si el proveedor elegido no es del nuevo tipo, se limpia
      const p = proveedores.find((a) => a._id === proveedor);
      if (p && p.telaHab !== nuevo) {
        setProveedor("");
        setPlazo("");
      }
    };
    if (!hayDatos) return aplicar();
    Swal.fire({
      title: "¿Cambiar el tipo?",
      text: "Se borrarán los renglones capturados",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Cancelar",
    }).then((r) => r.isConfirmed && aplicar());
  }

  // ---------- Renglones ----------
  const setRenglon = (id, cambios) =>
    setRenglones((prev) => prev.map((r) => (r.id === id ? { ...r, ...cambios } : r)));

  function agregaRenglon() {
    setRenglones((prev) => {
      if (tipo !== "Tela") return [...prev, nuevoRenglonHab()];
      // Copia tela y costo del último renglón para capturar rápido otro color
      const ult = prev[prev.length - 1];
      return [...prev, { ...nuevoRenglonTela(), telas: ult ? ult.telas : null, precio: ult ? ult.precio : "" }];
    });
  }

  const quitaRenglon = (id) =>
    setRenglones((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

  function eligeTela(id, tela) {
    // Precio: el del catálogo, editable
    setRenglon(id, { telas: tela ? tela._id : null, precio: tela ? (tela.costo != null ? tela.costo : "") : "" });
  }

  function eligeHab(id, hab) {
    setRenglon(id, { habilitacion: hab ? hab._id : null, precio: hab ? (hab.costo != null ? hab.costo : "") : "" });
  }

  // ---------- Rollos ----------
  const setRollos = (idRenglon, fn) =>
    setRenglones((prev) => prev.map((r) => (r.id === idRenglon ? { ...r, rollos: fn(r.rollos) } : r)));

  const cambiaRollo = (idRenglon, idRollo, cantidad) =>
    setRollos(idRenglon, (rs) => rs.map((x) => (x.id === idRollo ? { ...x, cantidad } : x)));

  function agregaRollo(idRenglon, despuesDe) {
    const nuevo = nuevoRollo();
    focoRef.current = `rollo-${nuevo.id}`;
    setRollos(idRenglon, (rs) => {
      const i = despuesDe ? rs.findIndex((x) => x.id === despuesDe) : rs.length - 1;
      const copia = [...rs];
      copia.splice(i + 1, 0, nuevo);
      return copia;
    });
  }

  const quitaRollo = (idRenglon, idRollo) =>
    setRollos(idRenglon, (rs) => (rs.length > 1 ? rs.filter((x) => x.id !== idRollo) : [nuevoRollo()]));

  function importaExcel(idRenglon, event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = ""; // permite volver a elegir el mismo archivo
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const filas = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        // Columna "cantidad" (sin importar mayúsculas)
        const cantidades = filas
          .map((f) => {
            const k = Object.keys(f).find((x) => String(x).trim().toLowerCase() === "cantidad");
            return k ? num(f[k]) : 0;
          })
          .filter((c) => c > 0);
        if (!cantidades.length) {
          Swal.fire({
            icon: "warning",
            title: "No se encontraron rollos",
            text: 'El Excel debe tener una columna "cantidad" con un rollo por fila',
          });
          return;
        }
        setRollos(idRenglon, (rs) => [
          ...rs.filter((x) => num(x.cantidad) > 0),
          ...cantidades.map((c) => nuevoRollo(c)),
        ]);
        Swal.fire({ icon: "success", title: `${cantidades.length} rollos importados`, timer: 1500, showConfirmButton: false });
      } catch (err) {
        console.log(err);
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo" });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // ---------- Guardar ----------
  function armaPayload() {
    if (!proveedor) return { error: "Selecciona el proveedor" };
    if (!fecha) return { error: "Captura la fecha" };
    const lista = [];
    for (const [i, r] of renglones.entries()) {
      const n = i + 1;
      if (num(r.precio) < 0 || String(r.precio).trim() === "") return { error: `Renglón ${n}: captura el costo` };
      if (tipo === "Tela") {
        if (!r.telas) return { error: `Renglón ${n}: selecciona la tela` };
        if (!r.colores) return { error: `Renglón ${n}: selecciona el color` };
        const rollos = r.rollos.map((x) => num(x.cantidad)).filter((c) => c > 0);
        if (!rollos.length) return { error: `Renglón ${n}: captura al menos un rollo` };
        lista.push({ telas: r.telas, colores: r.colores, precio: num(r.precio), rollos });
      } else {
        if (!r.habilitacion) return { error: `Renglón ${n}: selecciona la habilitación` };
        if (!(num(r.cantidad) > 0)) return { error: `Renglón ${n}: captura la cantidad` };
        lista.push({ habilitacion: r.habilitacion, precio: num(r.precio), cantidad: num(r.cantidad) });
      }
    }
    return {
      payload: {
        tipo,
        fecha,
        proveedores: proveedor,
        factura_proveedor: factura_proveedor || "NA",
        pais_origen: pais_origen || "NA",
        observaciones: observaciones || "NA",
        user: user.id,
        impuestos,
        impuestoPorcentaje: impuestos ? num(impuestosPor) : 0,
        renglones: lista,
      },
    };
  }

  async function guardar() {
    const { error, payload } = armaPayload();
    if (error) {
      Swal.fire({ icon: "warning", title: error });
      return;
    }
    const prov = proveedores.find((a) => a._id === proveedor) || {};
    const resumen =
      tipo === "Tela"
        ? `${fmt(totales.rollos)} rollos (${fmt(totales.metros)} mts / ${fmt(totales.kilos)} kg)`
        : `${payload.renglones.length} habilitaciones (${fmt(payload.renglones.reduce((t, x) => t + x.cantidad, 0))} unidades)`;
    const ok = await Swal.fire({
      title: "¿Registrar la recepción?",
      html: `Proveedor: <b>${prov.nombre_comercial || ""}</b><br/>${resumen}<br/>Total: <b>${money(totales.total)}</b><br/><br/>
             <small>Se genera la OC en automático, se carga al proveedor y entra al inventario.</small>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, registrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;

    setGuardando(true);
    try {
      const { data } = await axios.post(`${URL_COMPRAS}Directa`, payload, { headers });
      axios
        .post(
          URL_LOGS,
          { tipo: "Recepción directa", detalle: `Compra ${data.idCompras} / OC ${data.idOrdenCompra} ${tipo}`, user: user.id },
          { headers }
        )
        .catch(() => {});
      // Aviso breve y refresca la pantalla limpia para la siguiente recepción
      await Swal.fire({
        icon: "success",
        title: "Recepción registrada",
        html: `Compra <b>${data.idCompras}</b> · OC automática <b>${data.idOrdenCompra}</b><br/>Total ${money(data.total)}`,
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      window.location.reload();
    } catch (err) {
      const d = err.response && err.response.data;
      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        text: (d && d.message) || (typeof d === "string" ? d : "") || "Revisa la conexión",
      });
      setGuardando(false);
    }
  }

  // ---------- Opciones de los buscadores ----------
  const opcionesTelas = useMemo(
    () =>
      [...telas].sort((a, b) =>
        String(a.codigo || "").localeCompare(String(b.codigo || ""), undefined, { numeric: true })
      ),
    [telas]
  );
  const opcionesHab = useMemo(
    () => [...habilitaciones].sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""))),
    [habilitaciones]
  );
  const coloresOrden = useMemo(
    () => [...colores].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [colores]
  );

  if (!user.compras_create) {
    return (
      <>
        <Header />
      </>
    );
  }

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card container col-10">
        <Row>
          <Col md={2}>
            <Button size="sm" href="/ListadoCompras" className="btn btn-danger" id="botonListado">
              Regresar
            </Button>
          </Col>
        </Row>
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <i className="fas fa-truck" style={{ marginRight: 8, color: "#f59e0b" }} />
              {contenedor ? "Nueva Compra Contenedor" : "Nueva Compra"}
            </h3>
            <small className="text-muted">
              Recepción directa: la Orden de Compra se genera en automático.
            </small>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-header">
            <i className="fas fa-info-circle" />
            <span className="form-section-title">Datos Generales</span>
          </div>
          <div className="form-section-body">
            <Row>
              <Col md={2}>
                <Label>Tipo</Label>
                <Input type="select" value={tipo} onChange={(e) => cambiaTipo(e.target.value)}>
                  <option value="Tela">Tela</option>
                  <option value="Habilitacion">Habilitación</option>
                </Input>
              </Col>
              <Col md={4}>
                <Label>Proveedor {tipo === "Tela" ? "de tela" : "de habilitación"}</Label>
                <Autocomplete
                  size="small"
                  value={proveedoresDelTipo.find((a) => a._id === proveedor) || null}
                  options={proveedoresDelTipo}
                  getOptionLabel={(o) => o.nombre_comercial || ""}
                  getOptionSelected={(o, v) => o._id === v._id}
                  onChange={(e, sel) => cambiaProveedor(sel ? sel._id : "")}
                  noOptionsText={
                    proveedoresDelTipo.length
                      ? "Sin coincidencias"
                      : `No hay proveedores de ${tipo === "Tela" ? "tela" : "habilitación"}. Dalo de alta en Catálogos > Proveedores.`
                  }
                  renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca el proveedor" />}
                />
              </Col>
              <Col md={2}>
                <Label>Plazo (días)</Label>
                <Input value={plazo} disabled />
              </Col>
              <Col md={2}>
                <Label>Fecha</Label>
                <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </Col>
              <Col md={2}>
                <Label>Factura Proveedor</Label>
                <Input value={factura_proveedor} onChange={(e) => setFacturaProveedor(e.target.value)} />
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <Label>País Origen</Label>
                <Input value={pais_origen} onChange={(e) => setPaisOrigen(e.target.value)} />
              </Col>
              <Col md={4}>
                <Label>Observaciones</Label>
                <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
              </Col>
              <Col md={2}>
                <Label>&nbsp;</Label>
                <label
                  htmlFor="conIva"
                  style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    id="conIva"
                    checked={impuestos}
                    onChange={(e) => setImpuestos(e.currentTarget.checked)}
                  />
                  Costo más IVA
                </label>
              </Col>
              {impuestos && (
                <Col md={2}>
                  <Label>% IVA</Label>
                  <Input type="number" value={impuestosPor} onChange={(e) => setImpuestosPor(e.target.value)} />
                </Col>
              )}
            </Row>
          </div>
        </div>

        <br />

        {/* ---------------- Renglones ---------------- */}
        <div className="form-section">
          <div className="form-section-header">
            <i className={tipo === "Tela" ? "fas fa-scroll" : "fas fa-tags"} />
            <span className="form-section-title">{tipo === "Tela" ? "Telas y rollos" : "Habilitación"}</span>
          </div>
          <div className="form-section-body">
            {tipo === "Tela" &&
              renglones.map((r, idx) => {
                const tela = r.telas ? telaById.get(r.telas) : null;
                const vals = r.rollos.map((x) => num(x.cantidad)).filter((c) => c > 0);
                const cant = vals.reduce((s, c) => s + c, 0);
                return (
                  <div
                    key={r.id}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, marginBottom: 12 }}
                  >
                    <Row>
                      <Col md={4}>
                        <Label>Tela {idx + 1}</Label>
                        <Autocomplete
                          size="small"
                          value={tela}
                          options={opcionesTelas}
                          getOptionLabel={(o) => `${o.codigo || ""} ${o.nombre || ""}`.trim()}
                          getOptionSelected={(o, v) => o._id === v._id}
                          onChange={(e, sel) => eligeTela(r.id, sel)}
                          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca la tela" />}
                        />
                      </Col>
                      <Col md={3}>
                        <Label>Color</Label>
                        <Input
                          type="select"
                          value={r.colores}
                          onChange={(e) => setRenglon(r.id, { colores: e.target.value })}
                        >
                          <option value="">Selecciona</option>
                          {coloresOrden.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={1}>
                        <Label>Unidad</Label>
                        <Input value={tela ? tela.unidad : ""} disabled />
                      </Col>
                      <Col md={2}>
                        <Label>Costo</Label>
                        <Input
                          type="number"
                          step="any"
                          value={r.precio}
                          onChange={(e) => setRenglon(r.id, { precio: e.target.value })}
                        />
                      </Col>
                      <Col md={2} style={{ textAlign: "right" }}>
                        <Label>&nbsp;</Label>
                        <div>
                          <Button
                            size="sm"
                            color="danger"
                            disabled={renglones.length === 1}
                            onClick={() => quitaRenglon(r.id)}
                            title="Quitar renglón"
                          >
                            <i className="fas fa-trash" />
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ marginTop: 10 }}>
                      <Label style={{ marginRight: 8 }}>
                        Rollos ({vals.length}) · {fmt(cant)} {tela ? tela.unidad : ""} ·{" "}
                        {money(cant * num(r.precio))}
                      </Label>
                      <Button size="sm" color="info" onClick={() => agregaRollo(r.id)} tabIndex={-1}>
                        <i className="fas fa-plus" /> Rollo
                      </Button>{" "}
                      <Label className="btn btn-sm btn-success" style={{ marginBottom: 0 }} title='Excel con columna "cantidad", un rollo por fila'>
                        <i className="fas fa-file-excel" /> Importar Excel
                        <input
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          style={{ display: "none" }}
                          onChange={(e) => importaExcel(r.id, e)}
                        />
                      </Label>{" "}
                      {r.rollos.length > 1 && (
                        <Button
                          size="sm"
                          color="secondary"
                          tabIndex={-1}
                          onClick={() => setRenglon(r.id, { rollos: [nuevoRollo()] })}
                        >
                          Borrar rollos
                        </Button>
                      )}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {r.rollos.map((x, i) => (
                          <div key={x.id} style={{ display: "flex", alignItems: "center" }}>
                            <small style={{ width: 26, textAlign: "right", marginRight: 3, color: "#6b7280" }}>
                              {i + 1}
                            </small>
                            <Input
                              id={`rollo-${x.id}`}
                              bsSize="sm"
                              type="number"
                              step="any"
                              style={{ width: 80 }}
                              value={x.cantidad}
                              onChange={(e) => cambiaRollo(r.id, x.id, e.target.value)}
                              onKeyDown={(e) => {
                                // Enter = siguiente rollo (captura rápida)
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const sig = r.rollos[i + 1];
                                  if (sig) document.getElementById(`rollo-${sig.id}`).focus();
                                  else agregaRollo(r.id, x.id);
                                }
                              }}
                            />
                            <span
                              role="button"
                              title="Quitar rollo"
                              tabIndex={-1}
                              onClick={() => quitaRollo(r.id, x.id)}
                              style={{ cursor: "pointer", color: "#dc3545", marginLeft: 2, fontSize: 12 }}
                            >
                              <i className="fas fa-times" />
                            </span>
                          </div>
                        ))}
                      </div>
                      <small className="text-muted">Tip: escribe la cantidad y da Enter para pasar al siguiente rollo.</small>
                    </div>
                  </div>
                );
              })}

            {tipo === "Habilitacion" && (
              <>
                <Row>
                  <Col md={5}>
                    <Label>Habilitación</Label>
                  </Col>
                  <Col md={1}>
                    <Label>Unidad</Label>
                  </Col>
                  <Col md={2}>
                    <Label>Costo</Label>
                  </Col>
                  <Col md={2}>
                    <Label>Cantidad</Label>
                  </Col>
                  <Col md={1}>
                    <Label>Importe</Label>
                  </Col>
                </Row>
                {renglones.map((r) => {
                  const hab = r.habilitacion ? habById.get(r.habilitacion) : null;
                  return (
                    <Row key={r.id} style={{ marginBottom: 6 }}>
                      <Col md={5}>
                        <Autocomplete
                          size="small"
                          value={hab}
                          options={opcionesHab}
                          getOptionLabel={(o) => `${o.nombre || ""} ${o.descripcion || ""}`.trim()}
                          getOptionSelected={(o, v) => o._id === v._id}
                          onChange={(e, sel) => eligeHab(r.id, sel)}
                          renderInput={(params) => (
                            <TextField {...params} variant="outlined" placeholder="Busca la habilitación" />
                          )}
                        />
                      </Col>
                      <Col md={1}>
                        <Input value={hab ? hab.unidad : ""} disabled />
                      </Col>
                      <Col md={2}>
                        <Input
                          type="number"
                          step="any"
                          value={r.precio}
                          onChange={(e) => setRenglon(r.id, { precio: e.target.value })}
                        />
                      </Col>
                      <Col md={2}>
                        <Input
                          type="number"
                          step="any"
                          value={r.cantidad}
                          onChange={(e) => setRenglon(r.id, { cantidad: e.target.value })}
                        />
                      </Col>
                      <Col md={1} style={{ paddingTop: 8 }}>
                        {money(num(r.cantidad) * num(r.precio))}
                      </Col>
                      <Col md={1}>
                        <Button
                          size="sm"
                          color="danger"
                          disabled={renglones.length === 1}
                          onClick={() => quitaRenglon(r.id)}
                          tabIndex={-1}
                        >
                          <i className="fas fa-minus" />
                        </Button>
                      </Col>
                    </Row>
                  );
                })}
              </>
            )}

            <Button size="sm" color="info" onClick={agregaRenglon}>
              <i className="fas fa-plus" /> {tipo === "Tela" ? "Otra tela / color" : "Otra habilitación"}
            </Button>
          </div>
        </div>

        <Row style={{ marginTop: 12 }}>
          <Col md={12}>
            <h4 id="logoutBoton">
              TOTALES {fmt(totales.kilos)} kg. / {fmt(totales.metros)} mts. / {fmt(totales.piezas)} pzas.
              {tipo === "Tela" ? ` / ${fmt(totales.rollos)} rollos` : ""} · Subtotal {money(totales.subtotal)}
              {impuestos ? ` · IVA ${money(totales.iva)}` : ""} · Total {money(totales.total)}
            </h4>
          </Col>
        </Row>
        <Row style={{ marginBottom: 20 }}>
          <Col md={12}>
            {guardando ? (
              <Button className="btn btn-success" disabled>
                <Spinner size="sm" /> Guardando...
              </Button>
            ) : (
              <Button className="btn btn-success" onClick={guardar}>
                Guardar
              </Button>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
}

export default ComprasDirectaCreate;
